import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import {
  Users, BookOpen, School, Plus, UserPlus, Loader2,
  BarChart3, Settings, GraduationCap, ArrowRight, TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";
import { getCourses } from "@/api/lms/courses";
import { getAdminUsers, getAdminUsersCount, type AdminUserRow } from "@/api/admin/users";

function formatUserRole(role?: string): string {
  if (role === "SYSTEM_ADMIN") return "Admin";
  if (role === "TEACHER") return "Teacher";
  return "Student";
}

function userDisplayName(u: AdminUserRow): string {
  const fn = u.profile?.firstName ?? "";
  const ln = u.profile?.lastName ?? "";
  const full = `${fn} ${ln}`.trim();
  return full || u.email;
}

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [usersCountError, setUsersCountError] = useState<string | null>(null);
  const [courses, setCourses] = useState<Awaited<ReturnType<typeof getCourses>> | null>(null);
  const [users, setUsers] = useState<Awaited<ReturnType<typeof getAdminUsers>> | null>(null);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersCount, setUsersCount] = useState<number | null>(null);
  const [usersCountLoading, setUsersCountLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setUsersError(null);
      try {
        const c = await getCourses({ page: 1, limit: 50 });
        if (!cancelled) setCourses(c);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load courses");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    (async () => {
      setUsersCountLoading(true);
      setUsersCountError(null);
      try {
        const u = await getAdminUsers({ page: 1, limit: 12 });
        if (!cancelled) setUsers(u);
      } catch (e: unknown) {
        if (!cancelled) setUsersError(e instanceof Error ? e.message : "Failed to load users");
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();

    (async () => {
      try {
        const res = await getAdminUsersCount();
        if (!cancelled) setUsersCount(res.count);
      } catch (e: unknown) {
        if (!cancelled)
          setUsersCountError(e instanceof Error ? e.message : "Failed to load user count");
      } finally {
        if (!cancelled) setUsersCountLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(() => {
    const items = courses?.items ?? [];
    const totalEnrollments = items.reduce((sum, c) => sum + (c.enrollmentCount ?? 0), 0);
    const published = items.filter((c) => c.isPublished).length;
    return [
      {
        label: "Total Users",
        value: usersCountLoading
          ? "—"
          : usersCountError
            ? "Error"
            : usersCount !== null
              ? String(usersCount)
              : "—",
        sub: "Registered accounts",
        icon: Users,
        color: "bg-blue-50 text-blue-600",
      },
      {
        label: "Total Courses",
        value: courses ? String(courses.total) : "—",
        sub: `${published} published`,
        icon: BookOpen,
        color: "bg-primary/10 text-primary",
      },
      {
        label: "Enrollments",
        value: String(totalEnrollments),
        sub: "Across all courses",
        icon: School,
        color: "bg-emerald-50 text-emerald-600",
      },
    ];
  }, [courses, usersCount, usersCountLoading, usersCountError]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="w-full flex-1 p-10 flex justify-center items-center gap-2 text-muted-foreground">
          <Loader2 className="animate-spin" size={20} />
          <span className="text-sm">Loading admin dashboard…</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dash-page">

        {/* Page Header */}
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Admin Dashboard</h1>
            <p className="dash-page-subtitle">
              {error ? error : "Manage courses, users, and platform settings."}
            </p>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((s, i) => (
            <KCard key={i} className="dash-stat-card">
              <div className="flex items-start justify-between">
                <div className={`dash-stat-icon ${s.color}`}>
                  <s.icon size={18} />
                </div>
                <TrendingUp size={14} className="text-muted-foreground/40 mt-0.5" />
              </div>
              <div>
                <p className="dash-stat-value">{s.value}</p>
                <p className="dash-stat-label mt-1">{s.label}</p>
              </div>
              <p className="text-[11px] text-muted-foreground/70 font-medium">{s.sub}</p>
            </KCard>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

          {/* Courses Table */}
          <div>
            <div className="dash-section-header">
              <h2 className="dash-section-title">Courses</h2>
              <Link to="/admin/courses" className="dash-section-link flex items-center gap-1">
                View all <ArrowRight size={12} />
              </Link>
            </div>
            <KCard className="overflow-hidden">
              <table className="w-full dash-table">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Students</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(courses?.items ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="p-4">
                        <div className="dash-empty-state py-6 border-none bg-transparent shadow-none">
                          <p className="text-sm font-medium mb-1">No courses yet</p>
                          <Link to="/admin/create-course" className="text-primary text-xs hover:underline">
                            Create your first course
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    (courses?.items ?? []).slice(0, 8).map((c) => (
                      <tr key={c.id}>
                        <td>
                          <Link to={`/admin/courses/${c.id}/structure`} className="hover:text-primary transition-colors">
                            <p className="text-sm font-medium text-foreground leading-tight">{c.title}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {c.createdByUser?.email ?? "—"}
                            </p>
                          </Link>
                        </td>
                        <td>
                          <span className="text-sm text-foreground font-medium">
                            {c.enrollmentCount ?? 0}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`dash-badge ${
                              c.isPublished
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {c.isPublished ? "Published" : "Draft"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </KCard>
          </div>

          {/* Users Table */}
          <div>
            <div className="dash-section-header">
              <h2 className="dash-section-title">Recent Users</h2>
              <Link to="/admin/users" className="dash-section-link flex items-center gap-1">
                View all <ArrowRight size={12} />
              </Link>
            </div>
            <KCard className="overflow-hidden">
              <table className="w-full dash-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-12 text-sm text-muted-foreground text-center">
                        <Loader2 className="animate-spin inline mr-2" size={14} />
                        Loading users…
                      </td>
                    </tr>
                  ) : (users?.data ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="p-4">
                        <div className="dash-empty-state py-6 border-none bg-transparent shadow-none">
                          <p className="text-sm font-medium">
                            {usersError ? "No users loaded (check admin access)" : "No users found"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    (users?.data ?? []).map((u) => (
                      <tr key={u.id}>
                        <td>
                          <p className="text-sm font-medium text-foreground leading-tight">
                            {userDisplayName(u)}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{u.email}</p>
                        </td>
                        <td>
                          <span
                            className={`dash-badge ${
                              u.role === "TEACHER"
                                ? "bg-primary/10 text-primary"
                                : u.role === "SYSTEM_ADMIN"
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {formatUserRole(u.role)}
                          </span>
                        </td>
                        <td className="text-[11px] text-muted-foreground">
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </KCard>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <div className="dash-section-header">
            <h2 className="dash-section-title">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[
              { icon: BookOpen, label: "Course Catalog", to: "/admin/courses", color: "text-primary bg-primary/10" },
              { icon: UserPlus, label: "Assign Roles", to: "/admin/roles", color: "text-blue-600 bg-blue-50" },
              { icon: GraduationCap, label: "Mock Tests", to: "/mock-tests", color: "text-purple-600 bg-purple-50" },
              { icon: Settings, label: "System Settings", to: "/settings", color: "text-gray-600 bg-gray-100" },
            ].map((action) => (
              <Link key={action.to} to={action.to}>
                <div className="dash-action-card group">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${action.color} group-hover:scale-105 transition-transform duration-200`}>
                    <action.icon size={18} />
                  </div>
                  <span className="text-xs font-semibold text-foreground">{action.label}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
